import React, { useState } from 'react';
import {
  Checkbox,
  FormControlLabel,
  Typography,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Paper,
  IconButton,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';

const TermsContent = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  maxHeight: '60vh',
  overflowY: 'auto',
  '& h2': {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(1),
  },
  '& h3': {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  '& p': {
    marginBottom: theme.spacing(1),
  },
  '& ul': {
    marginLeft: theme.spacing(3),
    marginBottom: theme.spacing(1),
  },
}));

interface TermsCheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  error?: boolean;
  helperText?: string;
  variant?: 'signup' | 'invitation';
}

const TermsCheckbox: React.FC<TermsCheckboxProps> = ({
  checked,
  onChange,
  error = false,
  helperText,
  variant = 'signup',
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [termsVersion] = useState('1.0.0');

  const handleOpenDialog = (e: React.MouseEvent) => {
    e.preventDefault();
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleAcceptInDialog = () => {
    onChange(true);
    setDialogOpen(false);
  };

  const handleOpenPrivacy = (e: React.MouseEvent) => {
    e.preventDefault();
    // For now, open the terms dialog and scroll to privacy section
    // In production, this would link to a dedicated privacy policy page
    setDialogOpen(true);
    // Optionally scroll to privacy section after dialog opens
    setTimeout(() => {
      const privacySection = document.getElementById('privacy-section');
      if (privacySection) {
        privacySection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const getCheckboxLabel = () => {
    const action = variant === 'invitation' ? 'joining' : 'creating an account';
    return (
      <Typography variant="body2" color={error ? 'error' : 'textSecondary'}>
        I agree to the{' '}
        <Link href="#" onClick={handleOpenDialog} underline="always">
          Terms and Conditions
        </Link>{' '}
        and acknowledge that I have read the{' '}
        <Link href="#" onClick={handleOpenPrivacy} underline="always">
          Privacy Policy
        </Link>
        . By {action}, I consent to the processing of my personal data as described.
      </Typography>
    );
  };

  return (
    <>
      <div style={{ marginTop: 16, marginBottom: 8 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={checked}
              onChange={(e) => onChange(e.target.checked)}
              color="primary"
              size="small"
            />
          }
          label={getCheckboxLabel()}
          style={{
            alignItems: 'flex-start',
          }}
        />
        {error && helperText && (
          <Typography variant="caption" color="error" style={{ marginLeft: 32, display: 'block' }}>
            {helperText}
          </Typography>
        )}
      </div>

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle style={{ margin: 0, padding: 16 }}>
          Terms and Conditions
          <IconButton
            aria-label="close"
            onClick={handleCloseDialog}
            style={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: '#9e9e9e',
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <TermsContent elevation={0}>
            <Typography variant="h6" gutterBottom>
              Terms and Conditions of Service
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Version {termsVersion} - Effective Date: January 1, 2025
            </Typography>

            <Typography variant="h6" style={{ marginTop: 24 }}>
              1. Acceptance of Terms
            </Typography>
            <Typography variant="body2" paragraph>
              By accessing or using the Prismatic Forge Adaptive Productivity System, you agree to be bound by these Terms and Conditions.
            </Typography>

            <Typography variant="h6">2. Service Description</Typography>
            <Typography variant="body2" paragraph>
              Prismatic Forge provides an adaptive productivity system including knowledge management, AI-powered assistants, collaboration tools, and professional engineering tools.
            </Typography>

            <Typography variant="h6">3. User Accounts</Typography>
            <Typography variant="body2" paragraph>
              You must provide accurate information, maintain account security, and are responsible for all activities under your account.
            </Typography>

            <Typography variant="h6">4. Subscription and Billing</Typography>
            <Typography variant="body2" component="div">
              <ul>
                <li>Starter: $29/month per user</li>
                <li>Professional: $59/month per user</li>
                <li>Enterprise: $89/month per user</li>
                <li>Industry-Specific: $129/month per user</li>
              </ul>
              <Typography variant="body2" paragraph>
                All fees are non-refundable except as required by law.
              </Typography>
            </Typography>

            <Typography variant="h6">5. User Content and Data</Typography>
            <Typography variant="body2" paragraph>
              You retain ownership of your content. You grant us a limited license to process your content solely to provide the Service.
            </Typography>

            <Typography variant="h6">6. AI Services</Typography>
            <Typography variant="body2" paragraph>
              AI agents are provided "as-is" without guarantees of accuracy. You are responsible for reviewing AI-generated content and decisions.
            </Typography>

            <Typography variant="h6">7. Acceptable Use</Typography>
            <Typography variant="body2" paragraph>
              You agree not to use the Service for illegal purposes, to gain unauthorized access, or to interfere with the Service.
            </Typography>

            <Typography variant="h6">8. Privacy and Data Protection</Typography>
            <Typography variant="body2" paragraph>
              We process data in accordance with our Privacy Policy and comply with GDPR, CCPA, and other applicable data protection laws.
            </Typography>

            <Typography variant="h6">9. Limitation of Liability</Typography>
            <Typography variant="body2" paragraph>
              THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES. WE ARE NOT LIABLE FOR INDIRECT, INCIDENTAL, OR CONSEQUENTIAL DAMAGES.
            </Typography>

            <Typography variant="h6">10. Termination</Typography>
            <Typography variant="body2" paragraph>
              Either party may terminate at any time. Upon termination, you may export your data within 30 days.
            </Typography>

            <Typography variant="h6">11. Governing Law</Typography>
            <Typography variant="body2" paragraph>
              These Terms are governed by the laws of Delaware, USA. Disputes will be resolved through binding arbitration.
            </Typography>

            <Typography variant="h6">12. Contact Information</Typography>
            <Typography variant="body2" paragraph>
              Email: legal@prismaticforge.ai<br />
              Support: support@prismaticforge.ai
            </Typography>

            <Divider style={{ marginTop: 32, marginBottom: 32 }} />

            <Typography variant="h5" id="privacy-section" style={{ marginTop: 32, marginBottom: 16, scrollMarginTop: '20px' }}>
              Privacy Policy
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Last Updated: January 1, 2025
            </Typography>

            <Typography variant="h6" style={{ marginTop: 24 }}>1. Information We Collect</Typography>
            <Typography variant="body2" paragraph>
              We collect information you provide directly (name, email, organization details), usage data (features used, time spent), 
              and technical data (IP address, browser type, device information).
            </Typography>

            <Typography variant="h6">2. How We Use Your Information</Typography>
            <Typography variant="body2" paragraph>
              We use your information to provide and improve our services, personalize your experience, communicate with you, 
              ensure security and prevent fraud, and comply with legal obligations.
            </Typography>

            <Typography variant="h6">3. Data Storage and Security</Typography>
            <Typography variant="body2" paragraph>
              We use industry-standard encryption (AES-256) for data at rest and in transit. Your data is stored in secure, 
              SOC 2 compliant data centers. We implement regular security audits and penetration testing.
            </Typography>

            <Typography variant="h6">4. Data Sharing</Typography>
            <Typography variant="body2" paragraph>
              We do not sell your personal data. We may share data with service providers who assist in our operations, 
              when required by law, or with your explicit consent. Third-party integrations only access data you authorize.
            </Typography>

            <Typography variant="h6">5. Your Rights (GDPR/CCPA)</Typography>
            <Typography variant="body2" component="div">
              You have the right to:
              <ul>
                <li>Access your personal data</li>
                <li>Correct inaccurate data</li>
                <li>Request deletion of your data</li>
                <li>Export your data (data portability)</li>
                <li>Object to certain processing</li>
                <li>Opt-out of marketing communications</li>
              </ul>
            </Typography>

            <Typography variant="h6">6. Cookies and Tracking</Typography>
            <Typography variant="body2" paragraph>
              We use essential cookies for authentication and security. Analytics cookies help us improve our service. 
              You can manage cookie preferences in your browser settings.
            </Typography>

            <Typography variant="h6">7. Data Retention</Typography>
            <Typography variant="body2" paragraph>
              We retain your data for as long as your account is active or as needed to provide services. 
              After account deletion, we may retain certain data for legal compliance for up to 7 years.
            </Typography>

            <Typography variant="h6">8. Children's Privacy</Typography>
            <Typography variant="body2" paragraph>
              Our service is not intended for users under 16 years of age. We do not knowingly collect data from children.
            </Typography>

            <Typography variant="h6">9. International Data Transfers</Typography>
            <Typography variant="body2" paragraph>
              If we transfer data internationally, we ensure appropriate safeguards are in place, such as Standard Contractual Clauses 
              or adequacy decisions.
            </Typography>

            <Typography variant="h6">10. Contact Us</Typography>
            <Typography variant="body2" paragraph>
              For privacy concerns or to exercise your rights:<br />
              Email: privacy@prismaticforge.ai<br />
              Data Protection Officer: dpo@prismaticforge.ai
            </Typography>

            <Typography variant="body2" style={{ marginTop: 24, fontWeight: 'bold' }}>
              For the complete Terms and Conditions and Privacy Policy, please visit our website or contact support.
            </Typography>
          </TermsContent>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="inherit">
            Close
          </Button>
          <Button onClick={handleAcceptInDialog} variant="contained" color="primary">
            Accept Terms
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TermsCheckbox;